import { api } from './client';

/**
 * Materials — curated dashboard feed of YouTube videos + rich-text articles.
 * Managed by admin (global / per-marathon) and curators (own students); each
 * material can optionally award points once on first view (spec:
 * docs/superpowers/specs/2026-06-23-dashboard-materials-design.md).
 */

export type MaterialType = 'video' | 'article';
export type MaterialScope = 'global' | 'marathon' | 'curator';

export type Material = {
  id: string;
  type: MaterialType;
  title: string;
  description?: string;
  youtube_url?: string;
  body_html?: string;
  cover_url?: string;
  scope: MaterialScope;
  marathon_id?: string | null;
  curator_id?: string | null;
  points: number;
  is_active: boolean;
  display_order: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  /** Student-feed only: whether the current student has already viewed this. */
  viewed?: boolean;
};

/** Body for admin/curator create+update. Partial on PUT (no field-zeroing). */
export type MaterialInput = {
  type?: MaterialType;
  title?: string;
  description?: string;
  youtube_url?: string;
  body_html?: string;
  cover_url?: string;
  scope?: MaterialScope;
  marathon_id?: string | null;
  curator_id?: string | null;
  points?: number;
  is_active?: boolean;
  display_order?: number;
};

export type MarkViewedResult = {
  viewed: boolean;
  points_awarded: number;
};

export const materialsApi = {
  // ---- Student ----
  /** Visibility-filtered feed for the dashboard; each item carries `viewed`+`points`. */
  async listForStudent() {
    const { data } = await api.get('/courses/content/materials');
    // Envelope unwrap + list-normalize may alias the array to `.items`.
    const payload = data as Material[] | { items?: Material[]; materials?: Material[] };
    return (Array.isArray(payload) ? payload : payload.items ?? payload.materials ?? []) as Material[];
  },
  /** Mark viewed (+ award points once). Idempotent on the server. */
  async markViewed(id: string) {
    const { data } = await api.post(`/student/materials/${id}/view`);
    return data as MarkViewedResult;
  },

  // ---- Admin ----
  async listAdmin(params?: { scope?: MaterialScope; marathon_id?: string; type?: MaterialType }) {
    const qs = new URLSearchParams();
    if (params?.scope) qs.set('scope', params.scope);
    if (params?.marathon_id) qs.set('marathon_id', params.marathon_id);
    if (params?.type) qs.set('type', params.type);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const { data } = await api.get(`/admin/materials${suffix}`);
    const payload = data as Material[] | { items?: Material[]; materials?: Material[] };
    return (Array.isArray(payload) ? payload : payload.items ?? payload.materials ?? []) as Material[];
  },
  async getAdmin(id: string) {
    const { data } = await api.get(`/admin/materials/${id}`);
    return data as Material;
  },
  async createAdmin(body: MaterialInput) {
    const { data } = await api.post('/admin/materials', body);
    return data as Material;
  },
  async updateAdmin(id: string, body: MaterialInput) {
    const { data } = await api.put(`/admin/materials/${id}`, body);
    return data as Material;
  },
  async removeAdmin(id: string) {
    await api.delete(`/admin/materials/${id}`);
  },

  // ---- Curator ----
  async listCurator() {
    const { data } = await api.get('/curator/materials');
    const payload = data as Material[] | { items?: Material[]; materials?: Material[] };
    return (Array.isArray(payload) ? payload : payload.items ?? payload.materials ?? []) as Material[];
  },
  async createCurator(body: MaterialInput) {
    const { data } = await api.post('/curator/materials', body);
    return data as Material;
  },
  async updateCurator(id: string, body: MaterialInput) {
    const { data } = await api.put(`/curator/materials/${id}`, body);
    return data as Material;
  },
  async removeCurator(id: string) {
    await api.delete(`/curator/materials/${id}`);
  },
};
